/**
 * MultipleChoiceField.tsx
 *
 * Unified multiple choice field component with custom API support
 */

import React, { useState, useEffect } from 'react';
import { BaseFieldProps, BASE_INPUT_CLASSES, ERROR_INPUT_CLASSES, DISABLED_INPUT_CLASSES } from './shared/types';
import { FieldWrapper } from './shared';
import { API_BASE_URL } from '../../environments/environment';

export const MultipleChoiceField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
  formValues = {},
}) => {
  const isPreviewMode = mode === 'preview';
  const isDisabled = disabled || isPreviewMode;
  const [apiOptions, setApiOptions] = useState<Array<{ display: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Determine which options to use
  const hasCustomApi = fieldConfig.customApi?.enabled && fieldConfig.customApi?.url;
  const options = hasCustomApi ? apiOptions : (fieldConfig.options || []);

  // Extract a value from an object using dot notation path
  // Supports: product.label, product.id, label, etc.
  const extractValueFromObject = (obj: any, path: string): string => {
    if (!path) return JSON.stringify(obj);

    const parts = path.split('.');
    let result = obj;

    for (const part of parts) {
      if (result === null || result === undefined) return '';
      result = result[part];
    }

    return result !== null && result !== undefined ? String(result) : '';
  };

  // Extract data from response using enhanced dot notation with array indexing
  // Supports: data.items[0].name, data.items[*].name, data.array[1]
  const extractDataFromResponse = (data: any, path: string): any[] => {
    if (!path) return Array.isArray(data) ? data : [data];

    // Parse path to handle array indexing: e.g., "data.items[0].name" or "data.items[*].name"
    const pathParts: Array<{ key: string; index?: number | '*' }> = [];
    const regex = /([^\[\].]+)|\[(\d+|\*)\]/g;
    let match;

    while ((match = regex.exec(path)) !== null) {
      if (match[1]) {
        // Regular key
        pathParts.push({ key: match[1] });
      } else if (match[2]) {
        // Array index or wildcard
        const index = match[2] === '*' ? '*' : parseInt(match[2], 10);
        pathParts.push({ key: '', index });
      }
    }

    let result: any = data;

    for (const part of pathParts) {
      if (!result) return [];

      if (part.index !== undefined) {
        // Handle array indexing
        if (!Array.isArray(result)) return [];

        if (part.index === '*') {
          // Wildcard: continue processing for each item
          continue;
        } else {
          // Specific index
          result = result[part.index];
        }
      } else if (part.key) {
        // Handle object key access
        if (typeof result === "object" && part.key in result) {
          result = result[part.key];
        } else if (Array.isArray(result)) {
          // If result is array and we have a wildcard scenario, map over array
          const prevPart = pathParts[pathParts.indexOf(part) - 1];
          if (prevPart?.index === '*') {
            result = result.map(item =>
              item && typeof item === "object" && part.key in item ? item[part.key] : null
            ).filter(item => item !== null);
          } else {
            return [];
          }
        } else {
          return [];
        }
      }
    }

    // Flatten if result contains nested arrays
    const flattenDeep = (arr: any[]): any[] => {
      return arr.reduce((acc, val) =>
        Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val),
        []
      );
    };

    return Array.isArray(result) ? flattenDeep(result) : [result];
  };

  // Fetch options from custom API
  useEffect(() => {
    if (!hasCustomApi || mode === 'preview') return;

    const fetchOptions = async () => {
      setLoading(true);
      setApiError(null);

      try {
        const config = fieldConfig.customApi!;

        // Resolve path parameters: {placeholder} in URL gets replaced with field value
        const resolvePathParameters = (urlTemplate: string, pathParams: Record<string, string> | undefined): string | null => {
          if (!pathParams || Object.keys(pathParams).length === 0) {
            return urlTemplate;
          }

          let resolvedUrl = urlTemplate;

          Object.entries(pathParams).forEach(([placeholder, fieldId]) => {
            const fieldValue = formValues[fieldId];

            if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
              return; // Skip if field value is empty
            }

            resolvedUrl = resolvedUrl.replace(`{${placeholder}}`, encodeURIComponent(String(fieldValue)));
          });

          // Check if there are any unresolved placeholders
          if (resolvedUrl.includes('{')) {
            return null;
          }

          return resolvedUrl;
        };

        // Resolve dynamic query parameters that reference other field values
        // Supports format: {fieldId} to reference form values
        const resolveQueryParams = (params: Record<string, string> | undefined): Record<string, string> => {
          if (!params) return {};

          const resolved: Record<string, string> = {};
          Object.entries(params).forEach(([key, val]) => {
            if (typeof val === 'string' && val.startsWith('{') && val.endsWith('}')) {
              // Extract field ID from {fieldId} format
              const fieldId = val.slice(1, -1);
              const fieldValue = formValues[fieldId];

              if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
                // Skip this parameter if the referenced field has no value
                console.log(`Field reference ${val} not available (field value is empty)`);
                return;
              }

              console.log(`Resolved field reference ${val} to value: ${fieldValue}`);
              resolved[key] = String(fieldValue);
            } else {
              // Static value - use as is
              resolved[key] = val;
            }
          });
          return resolved;
        };

        // Resolve path parameters first
        const resolvedUrlPath = resolvePathParameters(config.url, config.pathParams);
        if (!resolvedUrlPath) {
          // Don't show error, just wait for path parameters to be filled
          // This is normal when dependent fields haven't been filled yet
          setApiOptions([]);
          setLoading(false);
          return;
        }

        const resolvedParams = resolveQueryParams(config.queryParams);

        // Build headers object
        const headers: Record<string, string> = {
          ...(config.headers || {}),
        };

        console.log('Fetching multiple choice options via backend proxy from:', resolvedUrlPath);

        // Use backend proxy to avoid CORS issues and keep API keys secure
        const proxyUrl = `${API_BASE_URL}/api/proxy/external-api`;
        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            url: resolvedUrlPath,
            method: config.method || 'GET',
            params: Object.keys(resolvedParams).length > 0 ? resolvedParams : undefined,
            headers: Object.keys(headers).length > 0 ? headers : undefined,
            body: config.method !== 'GET' && config.body ? config.body : undefined,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          // Extract data using response path
          const extractedData = extractDataFromResponse(data, config.responsePath || "");

          // Convert to display/value pairs
          const optionPairs = extractedData.map((item: any) => {
            const display = config.displayValuePath
              ? extractValueFromObject(item, config.displayValuePath)
              : typeof item === "string" ? item : JSON.stringify(item);

            const value = config.valuePath
              ? extractValueFromObject(item, config.valuePath)
              : typeof item === "string" ? item : JSON.stringify(item);

            return { display, value };
          });

          setApiOptions(optionPairs);
          setApiError(null);
          console.log(`Successfully fetched ${optionPairs.length} options`);
        } else {
          setApiError(`Failed to fetch options: ${response.status} ${response.statusText}`);
          console.error("API error:", response.status, response.statusText, data);
        }
      } catch (error: any) {
        setApiError(`Error: ${error.message}`);
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [
    hasCustomApi,
    fieldConfig.customApi?.url,
    fieldConfig.customApi?.method,
    fieldConfig.customApi?.responsePath,
    fieldConfig.customApi?.displayValuePath,
    fieldConfig.customApi?.valuePath,
    JSON.stringify(fieldConfig.customApi?.pathParams),
    JSON.stringify(fieldConfig.customApi?.queryParams),
    JSON.stringify(fieldConfig.customApi?.headers),
    fieldConfig.customApi?.body,
    JSON.stringify(formValues),
    mode,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (isPreviewMode) return;
    onChange(fieldConfig.id, e.target.value);
  };

  const selectClasses = `${BASE_INPUT_CLASSES} ${hasError ? ERROR_INPUT_CLASSES : ''} ${isDisabled ? DISABLED_INPUT_CLASSES : ''}`;

  const selectElement = (
    <>
      {apiError && mode !== 'preview' && (
        <div className="text-xs text-red-500 mb-2">
          {apiError}
        </div>
      )}
      <select
        id={fieldConfig.id}
        value={value || ''}
        onChange={handleChange}
        disabled={isDisabled || loading}
        className={selectClasses}
      >
        <option value="">{loading ? 'Loading options...' : 'Select an option'}</option>
        {options.map((option: any, idx: number) => {
          // Handle both string options (from fieldConfig.options) and object options (from API)
          const optionValue = typeof option === 'string' ? option : option.value;
          const optionDisplay = typeof option === 'string' ? option : option.display;

          return (
            <option key={idx} value={optionValue}>
              {optionDisplay}
            </option>
          );
        })}
      </select>
    </>
  );

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={isPreviewMode}
    >
      {selectElement}
    </FieldWrapper>
  );
});

MultipleChoiceField.displayName = 'MultipleChoiceField';
